import { useEffect, useMemo, useState } from "react";
import moment from "moment";
import {
    BreadcrumbsCommon,
    ClipLoaderCommon,
    StyledBorderButton,
    StyledButton,
    StyledSavedCartPage,
} from "@components";
import { AppModuleLayout } from "@layouts";
import { PATH_HOME, PATH_PENDING_REQUESTS, PATH_QUOTES } from "@routes";
import styled from "styled-components";
import {
    getAllBookings,
    fetchPointsHistory,
    getMetaData,
    selectAccount,
    selectCheckout,
    selectPoints,
    useAppDispatch,
    useAppSelector,
} from "@redux";
import { LoadingStage } from "src/Interfaces/shared";
import {
    deleteQuoteAndReload,
    loadAllQuotes,
    loadQuotes,
    loadSharedQuotes,
    selectQuotes,
} from "src/Redux/Slices/allQuotes";
import { IQuote } from "src/Interfaces/quotes";
import { useNavigate } from "react-router-dom";
import { getItemsPriceInfo, stripTimeZone } from "src/Module/Checkout/checkoutHelpers";
import { calcTotalUsablePoints } from "src/Module/Checkout/pointsHelpers";
import { discountApi } from "src/Api/discountApi";
import { fetchActiveDiscount } from "@module";
import {
    breakpoint,
    buttonGradient,
    theme,
    validateShareQuoteForm,
    SHARE_NAME_MAX_LENGTH,
} from "@utilities";
import { loadMyQuotes } from "src/Redux/Slices/myQuotes";
import { IBreadcrumbs } from "@interfaces";
import { bookingApi, quoteApi } from "@api";
import { ErrorToast } from "src/Components/toasts/ErrorToast";

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

// Helper function to truncate to 2 decimal places instead of rounding
const truncateToTwoDecimals = (value: number): string => {
    const factor = 100;
    const truncated = Math.floor(value * factor) / factor;
    return truncated.toFixed(2);
};
// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

type QuotesTab = "my-quotes" | "all-quotes" | "all-orders";

interface ModalState {
    isOpen: boolean;
    type?: string;
    quoteId?: string;
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const Quotes = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    // Selectors
    const quotes = useAppSelector((state) => state.allQuotes.quotes);
    const allQuotes = useAppSelector(selectQuotes);
    const allBookings = useAppSelector((state) => state.booking.allBookings);
    const { metadata, account } = useAppSelector(selectAccount);
    const myQuotes = useAppSelector((state) => state.myQuotes.quotes);
    const sharedToMeQuotes = useAppSelector((state) => state.allQuotes.sharedQuotes);
    const quotesLoadingStage = useAppSelector((state) => state.allQuotes.loadingStage);
    const isLoading = quotesLoadingStage === LoadingStage.IS_LOADING;
    const { commissionHistory } = useAppSelector(selectPoints);

    // State
    const [isDeleting, setIsDeleting] = useState(false);
    const [modalState, setModalState] = useState<ModalState>({ isOpen: false });
    const [activeTab, setActiveTab] = useState<QuotesTab>("my-quotes");
    const [loadingOrderId, setLoadingOrderId] = useState<number | null>(null);

    const isOrderTabEnabled = Boolean(metadata?.is_order_tab_enabled);

    // Computed quotes to show based on tab
    const computeQuotesToShow = () => {
        if (!isOrderTabEnabled) {
            return quotes && quotes.length ? quotes : (allQuotes?.allQuotes ?? []);
        }
        if (activeTab === "all-quotes") return allQuotes?.allQuotes ?? [];
        if (activeTab === "my-quotes") return myQuotes ?? [];
        return [];
    };
    const quotesToShow = computeQuotesToShow();

    // Breadcrumbs
    const QuotesBreadcrumbs: IBreadcrumbs[] = [
        { title: "Home", link: PATH_HOME },
        { title: isOrderTabEnabled ? "Orders" : "Quotes", link: PATH_QUOTES },
    ];

    // ============================================================================
    // EFFECTS
    // ============================================================================

    useEffect(() => {
        if (isOrderTabEnabled) {
            dispatch(loadAllQuotes());
            dispatch(loadMyQuotes());
            dispatch(getAllBookings());
        } else {
            dispatch(loadQuotes());
        }
        dispatch(loadSharedQuotes());
        dispatch(
            fetchPointsHistory({
                startDate: moment().subtract(1, "year").format("YYYY-MM-DD"),
                endDate: moment().format("YYYY-MM-DD"),
                untilTodayOnly: "0",
                onlyAvailablePoints: "0",
            }),
        );
    }, [dispatch, isOrderTabEnabled]);

    useEffect(() => {
        dispatch(getMetaData());
    }, [dispatch]);

    useEffect(() => {
        if (!isLoading) {
            window.scrollTo(0, 0);
        }
    }, [isLoading]);

    // ============================================================================
    // HANDLERS
    // ============================================================================

    const closeModal = () => {
        setModalState({ isOpen: false });
    };

    const handleDeleteQuote = async () => {
        const id = modalState.quoteId;
        if (!id) {
            console.warn("No quoteId set in modalState");
            return;
        }
        try {
            setIsDeleting(true);
            await dispatch(deleteQuoteAndReload(id)).unwrap();
            if (isOrderTabEnabled) {
                await Promise.all([
                    dispatch(loadAllQuotes()).unwrap(),
                    dispatch(loadMyQuotes()).unwrap(),
                    dispatch(getAllBookings()).unwrap(),
                ]);
            } else {
                await dispatch(loadQuotes()).unwrap();
            }
            setModalState({ isOpen: false });
        } catch (err) {
            console.error("Failed to delete quote:", err);
        } finally {
            setIsDeleting(false);
        }
    };

    const handleOpenDetail = (quote: IQuote): void => {
        navigate(`${PATH_QUOTES}/${quote.id}`);
    };

    const handleShowMore = async (orderId: number) => {
        try {
            setLoadingOrderId(orderId);
            const res = await bookingApi.getBookingVoucher(orderId);
            if (res.data.customerOrderPageUrl) {
                // Use location.href for mobile compatibility
                const isMobile =
                    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
                        navigator.userAgent,
                    );
                if (isMobile) {
                    window.location.href = res.data.customerOrderPageUrl;
                } else {
                    window.open(res.data.customerOrderPageUrl, "_blank");
                }
            } else {
                ErrorToast({ Message: "Voucher URL Not Available" });
            }
        } catch (error) {
            console.error("Failed to get booking voucher:", error);
            ErrorToast({ Message: "Failed To Load Voucher. Please Try Again." });
        } finally {
            setLoadingOrderId(null);
        }
    };

    const formatDate = (purchaseDate?: string | null): string => {
        if (!purchaseDate) return "N/A";
        return stripTimeZone(purchaseDate).local().format("DD MMM, YYYY");
    };

    const handlePendingRequestsClick = () => {
        navigate(PATH_PENDING_REQUESTS);
    };

    // ============================================================================
    // RENDER FUNCTIONS
    // ============================================================================

    const renderHeader = () => {
        if (isOrderTabEnabled) {
            return (
                <GroupedButton>
                    <ButtonContainer>
                        <TabButton
                            $active={activeTab === "my-quotes"}
                            $borderRadius={theme.borderRadius.voucher}
                            onClick={() => setActiveTab("my-quotes")}
                            type="button"
                        >
                            <p>My Quotes</p>
                        </TabButton>
                        <TabButton
                            $active={activeTab === "all-quotes"}
                            $borderRadius={theme.borderRadius.voucher}
                            onClick={() => setActiveTab("all-quotes")}
                            type="button"
                        >
                            <p>All Quotes</p>
                        </TabButton>
                        <TabButton
                            $active={activeTab === "all-orders"}
                            $borderRadius={theme.borderRadius.voucher}
                            onClick={() => setActiveTab("all-orders")}
                            type="button"
                        >
                            <p>All Orders</p>
                        </TabButton>
                    </ButtonContainer>
                    <PendingRequestsButton onClick={handlePendingRequestsClick}>
                        <span>Pending requests</span>
                        <NotificationBadge>{sharedToMeQuotes?.length || 0}</NotificationBadge>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                            <path
                                d="M9 18L15 12L9 6"
                                stroke={theme.colors.black}
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </PendingRequestsButton>
                </GroupedButton>
            );
        }

        return (
            <HeaderContainer>
                <div className="headings">
                    <p>Quotes</p>
                </div>
                <PendingRequestsButton onClick={handlePendingRequestsClick}>
                    <span>Pending requests</span>
                    <NotificationBadge>{sharedToMeQuotes?.length || 0}</NotificationBadge>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                        <path
                            d="M9 18L15 12L9 6"
                            stroke={theme.colors.black}
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </PendingRequestsButton>
            </HeaderContainer>
        );
    };

    const renderQuotes = () => {
        if (quotesToShow.length === 0) {
            return (
                <EmptyQuotesWrapper>
                    <EmptyQuotesTitle>You don't have any quotes yet.</EmptyQuotesTitle>
                    <EmptyQuotesSubtitle>You'll find all your quotes here.</EmptyQuotesSubtitle>
                    <EmptyQuotesButtonWrapper>
                        <ExploreTrip
                            color={theme.gradients.button}
                            onClick={() => navigate(PATH_HOME)}
                        >
                            Explore trips
                        </ExploreTrip>
                    </EmptyQuotesButtonWrapper>
                </EmptyQuotesWrapper>
            );
        }

        return quotesToShow.map((quote) => (
            <StyledSavedCartWrapper key={quote.id}>
                <QuoteContent
                    quote={quote}
                    onDelete={(quoteId) =>
                        setModalState({
                            isOpen: true,
                            type: "cancel",
                            quoteId: String(quoteId),
                        })
                    }
                    onOpenDetail={handleOpenDetail}
                />
            </StyledSavedCartWrapper>
        ));
    };

    const renderOrders = () => {
        if (allBookings.length === 0) {
            return (
                <div className="heading">
                    <p>You don't have any Orders</p>
                </div>
            );
        }

        return (
            <OrdersContainer>
                {allBookings.map((booking) => {
                    const hasMatchingCommission = (commissionHistory || []).some(
                        (commission) => commission.bookingReference === booking.bookingReference,
                    );
                    const hasCommission =
                        account?.is_points_displayed &&
                        metadata?.is_commission_agent &&
                        hasMatchingCommission;

                    return (
                        <div key={booking.orderId}>
                            <DateText>{booking.datePurchased}</DateText>
                            <StyledSavedCartWrapper $hasCommission={hasCommission}>
                                <StyledDateWrapper>
                                    <div className="product-container">
                                        <div className="bookingReference">
                                            Booking Ref : {booking.bookingReference}
                                        </div>
                                    </div>
                                    <StyledInfo>
                                        <div className="column-container">
                                            <span className="content">
                                                Starts On:
                                                <span className="content-data">
                                                    {formatDate(booking.earliestTravelDate)}
                                                </span>
                                            </span>
                                            <span className="content2">
                                                Total RRP:
                                                <span className="content-data">
                                                    ${booking.netTotal.toFixed(2)}
                                                </span>
                                            </span>
                                        </div>
                                        <div className="ButtonContainer">
                                            <BorderButton
                                                onClick={() => handleShowMore(booking.orderId)}
                                                disabled={loadingOrderId === booking.orderId}
                                                $textColor="#000000"
                                                color="transparent"
                                                $borderRadius={theme.borderRadius.small}
                                            >
                                                {loadingOrderId === booking.orderId ? (
                                                    <ClipLoaderCommon
                                                        color={theme.colors.highlight}
                                                        size={20}
                                                        margin={0}
                                                    />
                                                ) : (
                                                    "Open details"
                                                )}
                                            </BorderButton>
                                        </div>
                                    </StyledInfo>
                                </StyledDateWrapper>
                                {hasCommission && (
                                    <BookingCommissionLabel>
                                        You have received $
                                        {truncateToTwoDecimals(booking.commissionAmount)} Commission
                                    </BookingCommissionLabel>
                                )}
                            </StyledSavedCartWrapper>
                        </div>
                    );
                })}
            </OrdersContainer>
        );
    };

    // ============================================================================
    // MAIN RENDER
    // ============================================================================

    if (isLoading) {
        return (
            <AppModuleLayout>
                <ClipLoaderCommon color={theme.colors.highlight} size={70} margin={150} />
            </AppModuleLayout>
        );
    }
    // Show Quotes Page
    return (
        <AppModuleLayout>
            <StyledSavedCartPage>
                <div className="wrapper">
                    <BreadcrumbsWrapper>
                        <div className="breadCrumbs">
                            <BreadcrumbsCommon data={QuotesBreadcrumbs} />
                        </div>
                    </BreadcrumbsWrapper>

                    <div className="subWrapper">
                        <div className="listCarts">
                            {/* Always render the tab header with navigation */}
                            {renderHeader()}
                            {/* Show quotes for all tabs except 'all-orders' (includes saved, shared, pending quotes) */}
                            {activeTab !== "all-orders" && renderQuotes()}
                            {/* Show orders only when 'all-orders' tab is active */}
                            {activeTab === "all-orders" && renderOrders()}
                        </div>
                    </div>
                </div>

                {/* Delete Modal */}
                {modalState.isOpen && modalState.type === "cancel" && (
                    <ModalOverlay onClick={isDeleting ? undefined : closeModal}>
                        <ModalContainer onClick={(e) => e.stopPropagation()}>
                            <CloseButton
                                onClick={isDeleting ? undefined : closeModal}
                                disabled={isDeleting}
                            >
                                ×
                            </CloseButton>
                            <ModalHeader>
                                <h2>Cancel quote?</h2>
                            </ModalHeader>
                            <ModalContent>Are you sure you want to cancel this quote?</ModalContent>
                            {isDeleting ? (
                                <ClipLoaderCommon
                                    color={theme.colors.highlight}
                                    size={30}
                                    margin={0}
                                />
                            ) : (
                                <ButtonGroup>
                                    <YesButton onClick={handleDeleteQuote} disabled={isDeleting}>
                                        Yes, Cancel
                                    </YesButton>
                                    <NoButton onClick={closeModal}>No</NoButton>
                                </ButtonGroup>
                            )}
                        </ModalContainer>
                    </ModalOverlay>
                )}
            </StyledSavedCartPage>
        </AppModuleLayout>
    );
};

// ============================================================================
// QUOTE CONTENT COMPONENT
// ============================================================================

interface QuoteContentProps {
    quote: IQuote;
    onDelete: (quoteId: string | number) => void;
    onOpenDetail: (quote: IQuote) => void;
}

const QuoteContent = ({ quote, onDelete, onOpenDetail }: QuoteContentProps) => {
    const dispatch = useAppDispatch();
    const [orderDiscount, setOrderDiscount] = useState<number | undefined>();
    const [points, setPoints] = useState<number | undefined>();
    const [isOrderDiscountLoading, setIsOrderDiscountLoading] = useState(true);
    const [showHelp, setShowHelp] = useState(false);
    const [showSharedPopup, setShowSharedPopup] = useState(false);
    const [sentInvitations, setSentInvitations] = useState<any[]>([]);
    const [isLoadingInvitations, setIsLoadingInvitations] = useState(false);
    const [resendingEmail, setResendingEmail] = useState<string | null>(null);
    const [newShareEmail, setNewShareEmail] = useState("");
    const [newShareFirstName, setNewShareFirstName] = useState("");
    const [newShareLastName, setNewShareLastName] = useState("");
    const [isSharingNew, setIsSharingNew] = useState(false);
    const { account, metadata } = useAppSelector(selectAccount);
    const pointsData = useAppSelector(selectPoints).data;
    const { activeDiscount } = useAppSelector(selectCheckout);
    const isPointsAgent = metadata?.is_points_agent ?? false;
    const isCommissionAgent = metadata?.is_commission_agent ?? false;

    const originalPrice = useMemo(() => {
        if (quote.total_rrp !== undefined && quote.total_rrp !== null) {
            return quote.total_rrp;
        }
        return getItemsPriceInfo(quote.items).totalPrice;
    }, [quote.total_rrp, quote.items]);

    const discountedPrice = useMemo(() => {
        return (orderDiscount ?? 0) > 0
            ? originalPrice - (originalPrice * (orderDiscount ?? 0)) / 100
            : undefined;
    }, [orderDiscount, originalPrice]);

    const productNames = useMemo(() => {
        if (!quote.items?.length) return [];
        const names: string[] = [];
        quote.items.forEach((item) => {
            const name = item.product?.json?.name;
            if (name && !names.includes(name)) {
                names.push(name);
            }
        });
        return names;
    }, [quote.items]);

    const earliestTravelDate = useMemo(() => {
        if (!quote.items?.length) return null;
        const dates = quote.items
            .map((item) => item.start_date || item.booking_date)
            .filter(Boolean)
            .map((d) => moment(d))
            .filter((m) => m.isValid())
            .sort((a, b) => a.valueOf() - b.valueOf());
        return dates.length > 0 ? dates[0] : null;
    }, [quote.items]);

    useEffect(() => {
        if (!activeDiscount && !isPointsAgent && !isCommissionAgent) {
            dispatch(fetchActiveDiscount());
        }
    }, [activeDiscount, isPointsAgent, isCommissionAgent, dispatch]);

    useEffect(() => {
        const fetchDiscount = async () => {
            try {
                setIsOrderDiscountLoading(true);
                const response = await discountApi.getQuoteDiscount(quote.id.toString());
                setOrderDiscount(response.data.data.applicableDiscount);
                setPoints(response.data.data.pointsAvailable);
            } catch (error) {
                console.error(`Error fetching discount for quote ${quote.id}:`, error);
            } finally {
                setIsOrderDiscountLoading(false);
            }
        };
        fetchDiscount();
    }, [quote.id]);

    const handleResend = async (email: string) => {
        setResendingEmail(email);
        try {
            await quoteApi.ShareQuotesViaEmail({
                quoteId: quote.id.toString(),
                shareToEmail: email,
            });
        } catch (error: any) {
            const errorMessage = error?.response?.data?.title || "Failed to share quote";
            ErrorToast({ Message: errorMessage });
        } finally {
            setResendingEmail(null);
        }
    };

    const handleShareNew = async () => {
        const trimmed = newShareEmail.trim();
        if (!trimmed) return;
        const validation = validateShareQuoteForm({
            email: newShareEmail,
            firstName: newShareFirstName,
            lastName: newShareLastName,
        });
        if (!validation.valid) {
            ErrorToast({ Message: validation.message ?? "Invalid details" });
            return;
        }
        setIsSharingNew(true);
        try {
            await quoteApi.ShareQuotesViaEmail({
                quoteId: quote.id.toString(),
                shareToEmail: trimmed,
                firstName: newShareFirstName.trim(),
                lastName: newShareLastName.trim(),
            });
            setSentInvitations((prev) => [...prev, { email: trimmed }]);
            setNewShareEmail("");
            setNewShareFirstName("");
            setNewShareLastName("");
        } catch (error: any) {
            const errorMessage = error?.response?.data?.title || "Failed to share quote";
            ErrorToast({ Message: errorMessage });
        } finally {
            setIsSharingNew(false);
        }
    };

    return (
        <>
            {quote.shared_by_email && (
                <SharedByBanner>
                    <span>
                        Shared by: <strong>{quote.shared_by_email}</strong>
                    </span>
                </SharedByBanner>
            )}

            <StyledTopSection>
                <StyledQuoteTitle>{quote.title || "Untitled Quote"}</StyledQuoteTitle>
                <button onClick={() => onDelete(quote.id)} type="button">
                    <img src="/images/Gray-trash.svg" alt="delete" />
                </button>
            </StyledTopSection>

            <StyledSavedDate>
                Date: {quote.created_at ? moment(quote.created_at).format("DD/MM/YYYY") : "N/A"}
                <StyledVoucherInfo>
                    {quote.items?.length || 0} {quote.items?.length === 1 ? "item" : "items"}
                </StyledVoucherInfo>
            </StyledSavedDate>

            {productNames.length > 0 && (
                <QuoteProductList>
                    {productNames.map((name, idx) => (
                        <QuoteProductItem key={idx}>{name}</QuoteProductItem>
                    ))}
                </QuoteProductList>
            )}

            {earliestTravelDate && (
                <QuoteTravelDate>
                    Travel date: {earliestTravelDate.format("DD MMM, YYYY")}
                </QuoteTravelDate>
            )}

            <QuoteDivider />

            <StyledBottomWrapper>
                {showHelp && (
                    <div className="help-popup-overlay" onClick={() => setShowHelp(false)}>
                        <div className="help-popup-content" onClick={(e) => e.stopPropagation()}>
                            <p>
                                The final discount shown is based on the items in your cart that are
                                eligible for the offer. Some products may not qualify, while others
                                might have reduced discounts due to operator limitations. That's why
                                the final discount may be lower than expected — it's automatically
                                adjusted based on eligible items only.
                            </p>
                            <StyledButton
                                $borderRadius={theme.borderRadius.small}
                                color={theme.gradients.button}
                                onClick={() => setShowHelp(false)}
                            >
                                Got it
                            </StyledButton>
                        </div>
                    </div>
                )}

                <StyledActionsWrapper>
                    <PriceBlockStyled>
                        <div className="price-label-wrapper">
                            <span className="price-label">Price</span>
                            {account?.is_agent_integrated &&
                            account?.is_points_displayed &&
                            !metadata?.is_commission_agent ? (
                                <div className="price-discount-badge-wrapper">
                                    {isOrderDiscountLoading ? (
                                        <div className="loading-spinner"></div>
                                    ) : (
                                        <>
                                            <span className="price-discount-badge">
                                                {calcTotalUsablePoints(pointsData?.availableBalance, points)} pts
                                            </span>
                                            {(points ?? 0) > 0 && (
                                                <span className="price-earned-badge">
                                                    +{points} earned
                                                </span>
                                            )}
                                        </>
                                    )}
                                </div>
                            ) : (
                                !account?.is_agent_integrated && (
                                    <>
                                        {(activeDiscount?.discount ?? 0) > 0 && (
                                            <div className="price-discount-badge-wrapper">
                                                {isOrderDiscountLoading ? (
                                                    <div className="loading-spinner"></div>
                                                ) : (
                                                    (orderDiscount ?? 0) > 0 && (
                                                        <>
                                                            <span className="price-discount-badge">
                                                                {orderDiscount}% off
                                                            </span>
                                                            <QuestionMarkHelper>
                                                                <img
                                                                    onClick={() =>
                                                                        setShowHelp(true)
                                                                    }
                                                                    src="/images/HelpIcon.svg"
                                                                    alt="info"
                                                                />
                                                            </QuestionMarkHelper>
                                                        </>
                                                    )
                                                )}
                                            </div>
                                        )}
                                    </>
                                )
                            )}
                        </div>
                        {!isOrderDiscountLoading && (
                            <div className="price-value-wrapper">
                                {account?.is_agent_integrated ? (
                                    <span className="price-discounted-value">
                                        ${originalPrice.toFixed(2)}
                                    </span>
                                ) : (
                                    <>
                                        <span className="price-discounted-value">
                                            ${(discountedPrice ?? originalPrice).toFixed(2)}
                                        </span>
                                        {!!discountedPrice && originalPrice > discountedPrice && (
                                            <span className="price-original-value">
                                                ${originalPrice.toFixed(2)}
                                            </span>
                                        )}
                                    </>
                                )}
                            </div>
                        )}
                    </PriceBlockStyled>
                    <ProceedButton onClick={() => onOpenDetail(quote)} type="button">
                        Proceed
                    </ProceedButton>
                </StyledActionsWrapper>
            </StyledBottomWrapper>

            <QuoteActions>
                <SharesCount
                    onClick={async () => {
                        setShowSharedPopup(true);
                        setIsLoadingInvitations(true);
                        try {
                            const response = await quoteApi.getSentInvitations(quote.id.toString());
                            setSentInvitations(response.data.data);
                        } catch (error) {
                            console.error(
                                `Error fetching sent invitations for quote ${quote.id}:`,
                                error,
                            );
                        } finally {
                            setIsLoadingInvitations(false);
                        }
                    }}
                >
                    <img src="/images/user_share.svg" alt="Share count" width={18} height={18} />
                    <span>{quote.number_of_shares} shares</span>
                </SharesCount>
            </QuoteActions>

            {showSharedPopup && (
                <ShareModalOverlay onClick={() => setShowSharedPopup(false)}>
                    <ShareModalContent onClick={(e) => e.stopPropagation()}>
                        <ShareModalHeader>
                            <h3>Sent Invitations</h3>
                            <ShareCloseButton onClick={() => setShowSharedPopup(false)}>
                                <img
                                    src="/images/close_icon.svg"
                                    alt="Close"
                                    width={24}
                                    height={24}
                                />
                            </ShareCloseButton>
                        </ShareModalHeader>
                        <SharesListContainer>
                            {isLoadingInvitations ? (
                                <LoadingContainer>
                                    <div className="loading-spinner"></div>
                                </LoadingContainer>
                            ) : (
                                <>
                                    {sentInvitations
                                        .filter(
                                            (invitation, index, self) =>
                                                index ===
                                                self.findIndex((i) => i.email === invitation.email),
                                        )
                                        .map((invitation) => (
                                            <ShareItem key={invitation.email}>
                                                <ShareItemInfo>
                                                    <span className="email">
                                                        {invitation.email}
                                                    </span>
                                                </ShareItemInfo>
                                                <ResendButton
                                                    onClick={() => handleResend(invitation.email)}
                                                    disabled={resendingEmail === invitation.email}
                                                >
                                                    {resendingEmail === invitation.email
                                                        ? "Sending..."
                                                        : "Resend"}
                                                </ResendButton>
                                            </ShareItem>
                                        ))}
                                    {sentInvitations.length === 0 && (
                                        <EmptyState>No invitations sent yet</EmptyState>
                                    )}
                                </>
                            )}
                        </SharesListContainer>
                        <ShareInputRow>
                            <ShareNameInputsRow>
                                <ShareEmailInput
                                    type="text"
                                    placeholder="First name"
                                    value={newShareFirstName}
                                    onChange={(e) => setNewShareFirstName(e.target.value)}
                                    maxLength={SHARE_NAME_MAX_LENGTH}
                                    disabled={isSharingNew}
                                />
                                <ShareEmailInput
                                    type="text"
                                    placeholder="Last name"
                                    value={newShareLastName}
                                    onChange={(e) => setNewShareLastName(e.target.value)}
                                    maxLength={SHARE_NAME_MAX_LENGTH}
                                    disabled={isSharingNew}
                                />
                            </ShareNameInputsRow>
                            <ShareEmailRow>
                                <ShareEmailInput
                                    type="email"
                                    placeholder="Enter email to share"
                                    value={newShareEmail}
                                    onChange={(e) => setNewShareEmail(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") handleShareNew();
                                    }}
                                    disabled={isSharingNew}
                                />
                                <ShareNewButton
                                    onClick={handleShareNew}
                                    disabled={
                                        isSharingNew ||
                                        !newShareEmail.trim() ||
                                        !newShareFirstName.trim() ||
                                        !newShareLastName.trim()
                                    }
                                >
                                    {isSharingNew ? "Sharing..." : "Share"}
                                </ShareNewButton>
                            </ShareEmailRow>
                        </ShareInputRow>
                    </ShareModalContent>
                </ShareModalOverlay>
            )}
            {account?.is_points_displayed &&
                metadata?.is_commission_agent &&
                points !== undefined &&
                points > 0 &&
                quote.items?.length > 0 && (
                    <CommissionLabel>
                        You will earn $
                        {truncateToTwoDecimals((points ?? 0) / (pointsData?.pointsMultiplier ?? 1))}{" "}
                        Commission from this order
                    </CommissionLabel>
                )}
        </>
    );
};

// ============================================================================
// STYLED COMPONENTS
// ============================================================================

const StyledTopSection = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
`;

const StyledQuoteTitle = styled.div`
    font-size: 18px;
    font-weight: 600;
    font-family: "Plus Jakarta Sans", sans-serif;
    line-height: 120%;
    color: #000000;
`;

const BreadcrumbsWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    margin-bottom: 16px;

    .breadCrumbs {
        flex: 1;
    }
`;

const PendingRequestsButton = styled.button`
    display: flex;
    align-items: center;
    padding: 0;
    gap: 4px;
    background: transparent;
    border: none;
    cursor: pointer;
    transition: opacity 0.2s ease;

    ${breakpoint.breakOnlyMobile`
        align-self: flex-end;
    `}

    &:hover {
        opacity: 0.8;
    }

    span {
        font-family: "Poppins", sans-serif;
        font-weight: 500;
        font-size: 14px;
        line-height: 130%;
        color: ${theme.colors.items_color};
    }
`;

const NotificationBadge = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0 6px;
    min-width: 18px;
    height: 18px;
    background: #db340b;
    border-radius: 50%;
    font-family: "Poppins", sans-serif;
    font-weight: 600;
    font-size: 12px;
    color: #ffffff;
    text-align: center;
    aspect-ratio: 1;
`;

const SharesListContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 0px 0xp 16px 0px;
`;

const ShareItem = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 0px;
`;

const ShareItemInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    font-size: 20px;
    font-weight: 600;
    color: #000;
    line-height: 120%;

    ${breakpoint.breakOnlyMobile`
        width: 250px;
    `}

    .email {
        ${breakpoint.breakOnlyMobile`
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        `}
    }
`;

const ResendButton = styled.button`
    color: ${theme.colors.highlight};
    cursor: pointer;
    font-size: 16px;
    font-weight: 500;
    background: none;
    border: none;

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const EmptyState = styled.div`
    text-align: center;
    color: #6e7375;
    padding: 20px;
`;

const LoadingContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 20px;

    .loading-spinner {
        width: 50px;
        height: 50px;
        border: 2px solid #f3f3f3;
        border-top: 2px solid #31b4b9;
        border-radius: 50%;
        animation: spin 1s linear infinite;
    }

    @keyframes spin {
        0% {
            transform: rotate(0deg);
        }
        100% {
            transform: rotate(360deg);
        }
    }
`;

const StyledSavedCartWrapper = styled.div<{ $hasCommission?: boolean }>`
    background-color: white;
    margin-bottom: ${({ $hasCommission }) => ($hasCommission ? "0" : "24px")};
    border-radius: ${theme.borderRadius.medium};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    overflow: hidden;
`;

const SharedByBanner = styled.div`
    display: flex;
    justify-content: start;
    align-items: center;
    padding: 8px 14px;
    background: ${theme.colors.share_background};

    span {
        font-family: "Poppins", sans-serif;
        font-weight: 400;
        font-size: 12px;
        line-height: 120%;
        color: #6e7375;

        strong {
            font-weight: 400;
            color: ${theme.colors.highlight};
        }
    }
`;

const StyledSavedDate = styled.div`
    padding: 0 20px 16px;
    color: #6e7375;
    font-size: 12px;
    font-family: "Plus Jakarta Sans", sans-serif;
    display: flex;
    align-items: center;
    gap: 8px;
`;

const QuoteProductList = styled.div`
    padding: 0 20px 8px;
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const QuoteProductItem = styled.div`
    font-family: "Plus Jakarta Sans", sans-serif;
    font-size: 14px;
    font-weight: 500;
    color: #333;
    line-height: 140%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const QuoteTravelDate = styled.div`
    padding: 0 20px 12px;
    font-family: "Plus Jakarta Sans", sans-serif;
    font-size: 13px;
    color: #6e7375;
    line-height: 120%;
`;

const QuoteDivider = styled.div`
    width: calc(100% - 32px);
    height: 1px;
    background: #e6e9eb;
    margin: 0 16px;
`;

const StyledBottomWrapper = styled.div`
    padding: 16px 20px;
    gap: 10px;
    flex-direction: column;
    position: relative;

    .content {
        color: #4f4f4f;
        font-size: 14px;
    }

    .bookingReference {
        color: #131214;
        font-size: 18px;
        font-weight: bold;
    }

    .help-popup-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.5);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 5;
    }

    .help-popup-content {
        background: white;
        padding: 2rem;
        border-radius: ${theme.borderRadius.medium};
        max-width: 400px;
        ${breakpoint.breakMobile`
            width: 330px;
        `}

        p {
            text-align: center;
            line-height: 150%;
            font-family: poppins;
        }

        h3 {
            margin-bottom: 1rem;
        }

        button {
            margin-top: 1rem;
            padding: 8px 16px;
        }
    }
`;

const StyledVoucherInfo = styled.div`
    background-color: ${theme.colors.items_background};
    padding: 5px 12px;
    border-radius: 50px;
    font-size: 14px;
    font-family: "Plus Jakarta Sans", sans-serif;
    font-weight: 600;
    color: ${theme.colors.items_color};
`;

const StyledActionsWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;

    .ButtonContainer {
        display: flex;
        align-items: center;
        gap: 30px;
    }

    .CancelButton {
        font-size: 16px;
        font-weight: 600;
        color: ${theme.colors.main};
        background: none;
        border: none;
        cursor: pointer;
    }
`;

const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(9, 10, 24, 0.4);
    z-index: 1000;
    display: flex;
    justify-content: center;
    align-items: center;
`;

const ModalContainer = styled.div`
    background: #fff;
    width: 100%;
    max-width: 400px;
    border-radius: ${theme.borderRadius.medium};
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
    position: relative;
    padding: 50px 20px 60px;
    text-align: center;
`;

const ModalHeader = styled.div`
    margin-bottom: 30px;

    h2 {
        font-size: 20px;
        font-weight: 600;
        margin: 0;
    }
`;

const ModalContent = styled.div`
    color: #6e7375;
    font-size: 14px;
    margin-bottom: 20px;
`;

const ButtonGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
`;

const YesButton = styled.button`
    width: 50%;
    padding: 12px;
    background: linear-gradient(90deg, ${theme.gradients.button[0]}, ${theme.gradients.button[1]});
    color: ${theme.colors.button_text};
    font-weight: bold;
    border: none;
    border-radius: ${theme.borderRadius.small};
    cursor: pointer;
    margin: 0 auto;

    &:disabled {
        opacity: 0.7;
        cursor: not-allowed;
    }
`;

const NoButton = styled.button`
    width: 50%;
    padding: 12px;
    background: transparent;
    color: ${theme.colors.button_text1};
    font-weight: bold;
    border: 1px solid ${theme.colors.main};
    border-radius: ${theme.borderRadius.small};
    cursor: pointer;
    margin: 0 auto;
`;

const CloseButton = styled.button<{ disabled?: boolean }>`
    position: absolute;
    top: 15px;
    right: 15px;
    border: none;
    color: ${({ disabled }) => (disabled ? "#ccc" : "#000")};
    font-size: 24px;
    cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    line-height: 1;
`;

const PriceBlockStyled = styled.div`
    font-family: "Poppins", sans-serif;
    display: flex;
    flex-direction: column;
    gap: 4px;

    .price-label-wrapper {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        gap: 8px;

        .price-label {
            font-size: 16px;
            color: #6e7375;
            font-weight: 400;
            line-height: 120%;
            margin-right: 6px;
        }

        .price-discount-badge-wrapper {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 4px;

            .price-discount-badge {
                font-size: 0.75rem;
                padding: 3px 8px;
                background-color: ${theme.colors.main};
                color: ${theme.colors.button_text};
                border-radius: ${theme.borderRadius.large};
            }

            .loading-spinner {
                width: 16px;
                height: 16px;
                border: 2px solid #f3f3f3;
                border-top: 2px solid ${theme.colors.main};
                border-radius: 50%;
                animation: spin 1s linear infinite;
            }

            .price-earned-badge {
                font-size: 0.7rem;
                padding: 3px 8px;
                background-color: #e8f5e9;
                color: #2e7d32;
                border-radius: ${theme.borderRadius.large};
                font-weight: 500;
            }

            @keyframes spin {
                0% {
                    transform: rotate(0deg);
                }
                100% {
                    transform: rotate(360deg);
                }
            }
        }
    }

    .price-value-wrapper {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        gap: 8px;

        .price-discounted-value {
            font-size: 18px;
            font-weight: 500;
            line-height: 120%;
            color: #333333;
        }

        .price-original-value {
            font-size: 1rem;
            font-weight: 400;
            color: ${theme.colors.subtle};
            text-decoration: line-through;
        }
    }
`;

const QuestionMarkHelper = styled.div`
    cursor: pointer;
`;

const BorderButton = styled(StyledBorderButton)`
    min-width: 120px;
    min-height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0px 20px;
    font-size: 14px;
    font-weight: 500;
    border: 1px solid #d9d9d9;
    cursor: pointer;
    margin-top: 0px;
    font-family: plus jakarta sans;
`;

const ProceedButton = styled.button`
    padding: 6px 20px;
    background: linear-gradient(90deg, ${theme.gradients.button[0]}, ${theme.gradients.button[1]});
    color: ${theme.colors.button_text};
    font-weight: 600;
    font-size: 14px;
    border-radius: ${theme.borderRadius.voucher};
    cursor: pointer;
`;

const QuoteActions = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    padding: 0 20px 20px 20px;
    gap: 12px;
`;

const SharesCount = styled.div`
    cursor: pointer;
    transition: opacity 0.2s ease;

    &:hover {
        opacity: 0.8;
    }
    display: flex;
    align-items: center;
    padding: 0 8px;
    gap: 2px;
    height: 28px;
    background: #ffffff;
    border: 1px solid ${theme.colors.share_border};
    border-radius: 32px;

    span {
        font-family: "Poppins", sans-serif;
        font-weight: 500;
        font-size: 12px;
        line-height: 120%;
        color: ${theme.colors.button_text1};
    }
`;

const TabButton = styled(StyledButton)<{ $active?: boolean }>`
    padding: 8px 8px;
    min-width: 120px;
    background: ${({ $active }) =>
        $active
            ? `linear-gradient(90deg, ${theme.gradients.button[0]}, ${theme.gradients.button[1]})`
            : "white"};
    color: ${({ $active }) =>
        $active ? theme.colors.button_text : theme.colors.bordered_button_text};
    border: 1px solid
        ${({ $active }) => ($active ? "transparent" : theme.colors.bordered_button_color)};
    cursor: pointer;
    font-weight: 500;
    transition:
        background 180ms ease,
        color 180ms ease,
        box-shadow 180ms ease;
    height: 36px;

    p {
        margin: 0;
        font-size: 16px;
    }
`;

const ButtonContainer = styled.div`
    display: flex;
    justify-content: center;
    gap: 12px;
    align-items: center;
    margin-bottom: 20px;

    ${breakpoint.breakTablet`
        justify-content: flex-start;
    `}
`;

const DateText = styled.div`
    color: ${theme.colors.black};
    margin-bottom: 10px;
    font-size: 16px;
    font-weight: 600;
`;

const StyledDateWrapper = styled.div`
    padding: 16px 0;
    padding-left: 20px;
    padding-right: 25px;
    display: flex;
    color: ${theme.colors.muted};
    gap: 10px;
    flex-direction: column;
    position: relative;
    font-family: poppins;

    .product-container {
        display: flex;
        align-items: center;
        justify-content: space-between;
    }

    .bookingReference {
        display: flex;
        flex-direction: row;
        align-items: center;
        color: ${theme.colors.black};
        font-size: 18px;
        font-weight: 600;

        ${breakpoint.breakOnlyMobile`
            margin-left: 0px;
        `}
    }
`;

const StyledInfo = styled.div`
    display: flex;
    justify-content: space-between;

    .ButtonContainer {
        display: flex;
        align-items: flex-end;
        gap: 30px;
    }

    .column-container {
        display: flex;
        flex-direction: column;
        gap: 12px;
        flex: 1;

        .content {
            display: flex;
            gap: 6px;
            color: ${theme.colors.gray_2};
            font-size: 14px;

            .content-data {
                font-weight: 400;
            }
        }

        .content2 {
            display: flex;
            flex-direction: column;
            gap: 6px;
            color: ${theme.colors.gray_2};
            font-size: 16px;

            .content-data {
                font-weight: 500;
                font-size: 18px;
                color: ${theme.colors.gray_1};
            }
        }

        ${breakpoint.breakOnlyMobile`
            flex-direction: column;
            gap: 8px;

            button {
                border: 1px solid ${theme.colors.black};
            }
        `}
    }
`;

const ShareModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;

    ${breakpoint.breakOnlyMobile`
        align-items: flex-end;
    `}
`;

const ShareModalContent = styled.div`
    background-color: white;
    padding: 20px;
    border-radius: 8px;
    width: 570px;

    p {
        font-family: "Plus Jakarta Sans", sans-serif;
        font-size: 18px;
        font-weight: 600;
    }
`;

const ShareModalHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    h3 {
        font-size: 28px;
        font-weight: 600;
        width: calc(100% - 40px);

        ${breakpoint.breakOnlyMobile`
            font-size: 20px;    
        `}
    }
    margin-bottom: 20px;
`;

const ShareCloseButton = styled.button`
    cursor: pointer;
`;

const ShareInputRow = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid #e6e9eb;
`;

const ShareNameInputsRow = styled.div`
    display: flex;
    gap: 8px;
`;

const ShareEmailRow = styled.div`
    display: flex;
    gap: 8px;
`;

const ShareEmailInput = styled.input`
    flex: 1;
    padding: 10px 12px;
    border: 1px solid #e6e9eb;
    border-radius: 8px;
    font-size: 14px;
    font-family: "Poppins", sans-serif;
    outline: none;

    &:focus {
        border-color: ${theme.colors.highlight};
    }

    &:disabled {
        opacity: 0.6;
    }
`;

const ShareNewButton = styled.button`
    padding: 10px 20px;
    background: ${buttonGradient(theme.gradients.button)};
    color: ${theme.colors.button_text};
    border: none;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    font-family: "Poppins", sans-serif;
    white-space: nowrap;

    &:hover:not(:disabled) {
        opacity: 0.9;
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const HeaderContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;

    .headings {
        font-size: 20px;
        font-weight: 700;
        line-height: 36px;
        display: flex;
        align-items: center;
        gap: 30px;

        ${breakpoint.breakIpadPro`
            font-size: 28px;
        `}
    }
`;

const GroupedButton = styled.div`
    display: flex;
    justify-content: space-between;

    ${breakpoint.breakOnlyMobile`
        flex-direction: column-reverse;
        margin-bottom: 20px;  
    `}
`;

const EmptyQuotesWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 350px;
    border-radius: ${theme.borderRadius.large};
    margin: 40px 0;
`;

const EmptyQuotesTitle = styled.div`
    font-weight: 700;
    font-size: 1rem;
    text-align: center;
    margin-bottom: 12px;
    color: #222;
`;

const EmptyQuotesSubtitle = styled.div`
    color: #9e9e9e;
    font-size: 1rem;
    margin-bottom: 32px;
    text-align: center;
`;

const EmptyQuotesButtonWrapper = styled.div`
    width: 320px;
    max-width: 100%;
`;

const ExploreTrip = styled(StyledButton)`
    width: 100%;
    height: 50px;
    font-size: 16px;
    font-weight: 600;
`;

const CommissionLabel = styled.div`
    background: linear-gradient(90deg, #fffef9 0%, #f0edff 100%);
    color: #5336e2;
    padding: 12px 20px;
    text-align: center;
    font-size: 14px;
    font-weight: 500;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

    ${breakpoint.breakTablet`
        background: #F0E9FF;
    `}
`;

const BookingCommissionLabel = styled.div`
    background: #f0e9ff;
    color: #5336e2;
    padding: 12px 20px;
    text-align: center;
    font-size: 14px;
    font-weight: 500;
    border-radius: 0 0 ${theme.borderRadius.small} ${theme.borderRadius.small};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const OrdersContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
`;
